import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

interface Pillar {
  pillar: string;
  product: string;
  icon: React.ReactNode;
  color: string;
  colorLight: string;
  colorMedium: string;
  benefit: string;
  ageBadge: string;
  cta: string;
  link: string;
}

const pillars: Pillar[] = [
  {
    pillar: 'Corps',
    product: 'Neurogames',
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714a2.25 2.25 0 00.659 1.591L19 14.5m-4.75-11.396c.251.023.501.05.75.082M12 3v2.25m0 0a2.25 2.25 0 002.25 2.25M12 5.25a2.25 2.25 0 01-2.25 2.25m4.5 0a2.25 2.25 0 012.25 2.25v1.5m-9 0a2.25 2.25 0 00-2.25 2.25v1.5m0 0h13.5"
        />
        <circle cx="12" cy="18" r="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    color: '#F59E0B',
    colorLight: 'rgba(245, 158, 11, 0.08)',
    colorMedium: 'rgba(245, 158, 11, 0.15)',
    benefit: 'Préparer le cerveau aux apprentissages par le mouvement',
    ageBadge: '3-6 ans',
    cta: 'Découvrir Neurogames',
    link: '/programme-neurogames',
  },
  {
    pillar: 'Coeur',
    product: 'Soft Skills',
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 15a3.75 3.75 0 11-7.5 0M12 12v3"
        />
      </svg>
    ),
    color: '#0D9488',
    colorLight: 'rgba(13, 148, 136, 0.08)',
    colorMedium: 'rgba(13, 148, 136, 0.15)',
    benefit: "Cultive l'intelligence émotionnelle et les compétences sociales",
    ageBadge: 'CP à CE6',
    cta: 'Découvrir Soft Skills',
    link: '/programme-soft-skills',
  },
  {
    pillar: 'Esprit',
    product: 'Lumi',
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"
        />
      </svg>
    ),
    color: '#00A3FF',
    colorLight: 'rgba(0, 163, 255, 0.08)',
    colorMedium: 'rgba(0, 163, 255, 0.15)',
    benefit: "Un tuteur IA personnel pour accompagner chaque leçon",
    ageBadge: 'CP-CE6',
    cta: 'Découvrir Lumi',
    link: '/revision',
  },
  {
    pillar: 'Avenir',
    product: 'Programme IA',
    icon: (
      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        {/* Robot head with antenna */}
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.5 8.25h9a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5h-9a1.5 1.5 0 01-1.5-1.5v-6a1.5 1.5 0 011.5-1.5z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8.25V5.25" />
        <circle cx="12" cy="4.5" r="1" fill="currentColor" />
        <circle cx="9.75" cy="12" r="1" fill="currentColor" />
        <circle cx="14.25" cy="12" r="1" fill="currentColor" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 14.5c.5.5 1.25.75 2 .75s1.5-.25 2-.75" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 11.25H4.5M19.5 11.25H18M6 14.25H4.5M19.5 14.25H18" />
      </svg>
    ),
    color: '#7C3AED',
    colorLight: 'rgba(124, 58, 237, 0.08)',
    colorMedium: 'rgba(124, 58, 237, 0.15)',
    benefit: "Initie nos élèves à l'intelligence artificielle, pour grandir en confiance dans le monde de demain",
    ageBadge: 'Primaire',
    cta: 'Découvrir le Programme IA',
    link: '/programme-ia',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const InnovationSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ['-5%', '5%']);
  const headingY = useTransform(scrollYProgress, [0, 0.5], ['20px', '0px']);
  const headingOpacity = useTransform(scrollYProgress, [0, 0.25], [0.6, 1]);

  return (
    <section
      ref={sectionRef}
      className="relative py-24 lg:py-32 overflow-hidden bg-gradient-to-b from-white via-gray-50/80 to-white"
    >
      {/* Decorative background */}
      <motion.div className="absolute inset-0 pointer-events-none" style={{ y: bgY }}>
        <div className="absolute top-20 left-[8%] w-72 h-72 bg-amber-100/40 rounded-full blur-3xl" />
        <div className="absolute top-40 left-[40%] w-64 h-64 bg-teal-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-32 right-[18%] w-80 h-80 bg-blue-100/25 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-[5%] w-64 h-64 bg-violet-100/30 rounded-full blur-3xl" />
        {/* Subtle dot grid */}
        <div className="absolute inset-0 opacity-[0.03]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="innovation-dots" width="32" height="32" patternUnits="userSpaceOnUse">
                <circle cx="16" cy="16" r="1.5" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#innovation-dots)" />
          </svg>
        </div>
      </motion.div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <motion.div
          className="text-center max-w-2xl mx-auto mb-16 lg:mb-20"
          style={{ y: headingY, opacity: headingOpacity }}
        >
          <motion.span
            className="inline-flex items-center gap-2 bg-primary/10 text-primary-dark px-4 py-2 rounded-full text-sm font-semibold tracking-wide mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="w-2 h-2 bg-primary rounded-full" />
            L'approche globale GS Sanaa
          </motion.span>

          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight leading-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Corps, Coeur, Esprit,{' '}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent">
                Avenir
              </span>
              <svg
                className="absolute -bottom-2 left-0 w-full h-3 text-secondary/30"
                viewBox="0 0 200 12"
                preserveAspectRatio="none"
              >
                <path
                  d="M0 8 Q50 0 100 8 T200 8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="8"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </motion.h2>

          <motion.p
            className="mt-5 text-lg text-gray-500 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Quatre piliers innovants pour une éducation complète qui prépare chaque enfant à s'épanouir pleinement et à grandir en confiance dans le monde de demain.
          </motion.p>
        </motion.div>

        {/* Cards grid */}
        <motion.div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
        >
          {pillars.map((pillar) => (
            <motion.div
              key={pillar.pillar}
              variants={cardVariants}
              whileHover={{ y: -8, transition: { duration: 0.3, ease: 'easeOut' } }}
              className="group relative"
            >
              <div
                className="relative h-full bg-white rounded-3xl border border-gray-100 shadow-lg shadow-gray-200/50 overflow-hidden transition-shadow duration-500 group-hover:shadow-2xl group-hover:shadow-gray-300/40 group-hover:border-gray-200"
              >
                {/* Colored top accent bar */}
                <div
                  className="h-1.5 w-full"
                  style={{ background: `linear-gradient(90deg, ${pillar.color}, ${pillar.color}88)` }}
                />

                {/* Card content */}
                <div className="p-8 lg:p-10">
                  {/* Icon area */}
                  <div className="mb-8">
                    <div
                      className="relative inline-flex items-center justify-center w-20 h-20 rounded-2xl transition-transform duration-500 group-hover:scale-110"
                      style={{ backgroundColor: pillar.colorLight }}
                    >
                      <div style={{ color: pillar.color }}>
                        {pillar.icon}
                      </div>
                      {/* Decorative ring */}
                      <div
                        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                        style={{ boxShadow: `0 0 0 4px ${pillar.colorMedium}` }}
                      />
                    </div>
                  </div>

                  {/* Pillar label */}
                  <h3
                    className="text-2xl lg:text-3xl font-bold tracking-tight mb-1"
                    style={{ color: pillar.color }}
                  >
                    {pillar.pillar}
                  </h3>

                  {/* Product name */}
                  <p className="text-lg font-semibold text-gray-900 mb-4">
                    {pillar.product}
                  </p>

                  {/* Age badge */}
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold tracking-wide mb-5"
                    style={{
                      backgroundColor: pillar.colorLight,
                      color: pillar.color,
                    }}
                  >
                    {pillar.ageBadge}
                  </span>

                  {/* Benefit text */}
                  <p className="text-gray-500 leading-relaxed mb-8 min-h-[3rem]">
                    {pillar.benefit}
                  </p>

                  {/* CTA button */}
                  <Link
                    to={pillar.link}
                    className="group/btn inline-flex items-center gap-2 font-semibold text-sm transition-all duration-300"
                    style={{ color: pillar.color }}
                  >
                    <span className="relative">
                      {pillar.cta}
                      <span
                        className="absolute -bottom-0.5 left-0 w-0 h-0.5 group-hover/btn:w-full transition-all duration-300"
                        style={{ backgroundColor: pillar.color }}
                      />
                    </span>
                    <svg
                      className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </Link>
                </div>

                {/* Hover glow effect */}
                <div
                  className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-700 pointer-events-none"
                  style={{ backgroundColor: pillar.color }}
                />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default InnovationSection;
